import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import Document from "documents/document";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import AHBoolean from "_shared/booleans/boolean";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/__session/session-context";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import WorkDocument from "work/work-agreements/contracts/work-document";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField, { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import DocumentRedlineSelection from "work/proposals/redlining/field-redlines/_views/document-redline-selection";
import SwitchRedline from "work/proposals/redlining/field-redlines/_views/switch-redline";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import downloadFile from "_shared/_utilities/download-file";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentSelector from "documents/_views/selectors/document-selector";
import DocumentUploadOverlay from "documents/_views/uploader/document-upload-overlay";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import { Masked } from "work/_shared/masked-fields";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== "waived",
})<{ waived: boolean }>(({ waived }) => ({
  flexWrap: "nowrap",
  height: "100%",
  minHeight: "20rem",
  opacity: waived ? 0.25 : 1,
  pointerEvents: waived ? "none" : "auto",
}));
const DocumentSelectorContainer = styled(ResizableBox)(() => ({
  display: "flex",
  position: "relative"
}));
const SelectedDocsList = styled(DocumentRedlineSelection)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  flex: 1,
  maxHeight: "60vh",
  marginLeft: theme.spacing(2),
  position: "relative",
  "& .MuiListItem-root": {
    maxWidth: "550px",
  }
}));
const HeadControls = styled("div")(({ theme }) => ({
  alignItems: "baseline",
  display: "flex",
  justifyContent: "space-between",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));
const Uploader = styled(DocumentUploadOverlay)(() => ({
  backgroundColor: "rgba(250, 250, 250, 0.5)",
  backdropFilter: "blur(5px) saturate(200%)",
  position: "absolute",
  top: 0,
  left: 0,
  margin: "1rem",
  width: "calc(100% - 2rem)",
  height: "calc(100% - 2rem)",
  zIndex: 9999,
}));
const Change = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s infinite` : "none",
    border: active ? "1px solid" : 0,
    borderColor: theme.palette.primary.main,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
  })
);
const IssueContainer = styled("div", { shouldForwardProp: (prop) => prop !== "active" })<{ active?: boolean }>(
  ({ theme, active }) => ({
    animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
    margin: active ? theme.spacing(1) : "initial",
    marginLeft: 0,
    marginTop: active ? 0 : theme.spacing(1),
    padding: active ? theme.spacing(1) : "initial",
    borderRadius: theme.shape.borderRadius,
  })
);

type ConflictsTabProps = {
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  commentThreads: CommentThread[];
  disableEditing?: boolean;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField: (fieldInfo: ProposalField | undefined) => void;
  commentsToolTip: string;
  onCommentsClicked: (field: ProposalField, name?: string) => void;
};

const ConflictsTab = (props: Readonly<ConflictsTabProps>) => {
  const {
    proposalRedline,
    activeReviewField,
    commentThreads,
    disableEditing,
    onProposalRedlineChange,
    onTraverseToNewField,
    commentsToolTip,
    onCommentsClicked,
  } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);
  const [documentIdToReplace, setDocumentIdToReplace] = React.useState<Guid | null>(null);
  const [creatingDocumentFromTemplate, setCreatingDocumentFromTemplate] = React.useState<Guid | null>(null);

  const issues = React.useMemo(
    () =>
      props.issues?.entries.filter((issue) =>
        [
          ProposalField.Conflicts,
          ProposalField.ConflictsDocument(issue.field.id ?? Guid.EMPTY),
          ProposalField.WaiveConflictsCheck,
        ].includes(issue.field)
      ),
    [props.issues?.entries]
  );

  const session = useSession();

  async function handleDownloadDocumentById(
    documentId?: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event?.stopPropagation();
    if (!documentId) return;

    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const conflictsTemplate = await documentApiService.downloadDocument(documentId);
      await downloadFile(conflictsTemplate);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to download conflicts template. Please try again", { variant: "error" });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  function handleDocumentRedlineChanged(
    newDocumentsRedline: FieldRedlineArray<WorkDocument>,
    traversalFieldOverride?: ProposalField
  ) {
    let updatedProposalRedline = proposalRedline.updateConflictsRedline(newDocumentsRedline);
    if (Masked.isMasked(updatedProposalRedline.conflictsCheckWaived))
      throw new Error("Conflicts Check Waived cannot be a Masked Contract");

    if (
      newDocumentsRedline.redlines.some((documentRedline) => documentRedline.currentEntry) &&
      !updatedProposalRedline.conflictsCheckWaived.currentEntry?.isEqualTo(new AHBoolean(false))
    ) {
      updatedProposalRedline = updatedProposalRedline.updateConflictsCheckWaivedRedline(
        updatedProposalRedline.conflictsCheckWaived.edit(new AHBoolean(false))
      );
    }
    onProposalRedlineChange(updatedProposalRedline, traversalFieldOverride);
  }

  function handleDocumentSelectionChanged(selectedDocs: Document[], deselectedDocs: Document[]) {
    if (!selectedDocs.length && !deselectedDocs.length) return;

    let updatedProposalRedline = proposalRedline;
    if (Masked.isMasked(updatedProposalRedline.conflictsCheckWaived)) {
      throw new Error("Conflicts Check Waived cannot be a Masked Contract");
    }

    if (
      selectedDocs.length &&
      !updatedProposalRedline.conflictsCheckWaived.currentEntry?.isEqualTo(new AHBoolean(false))
    ) {
      updatedProposalRedline = updatedProposalRedline.updateConflictsCheckWaivedRedline(
        updatedProposalRedline.conflictsCheckWaived.edit(new AHBoolean(false))
      );
    }

    for (const selectedDoc of selectedDocs) {
      if (Masked.isMasked(updatedProposalRedline.conflicts)) {
        throw new Error("Conflicts Documents cannot be a Masked Contract");
      }

      const workDocument = WorkDocument.fromDocument(selectedDoc, WorkDocumentType.Conflicts);
      if (
        !updatedProposalRedline.conflicts.redlines.some((document) => document.currentEntry?.is(workDocument))
      ) {
        updatedProposalRedline = updatedProposalRedline.updateConflictsRedline(
          updatedProposalRedline.conflicts.addEntry(workDocument)
        );
      }
    }

    for (const deselectedDoc of deselectedDocs) {
      if (Masked.isMasked(updatedProposalRedline.conflicts)) {
        throw new Error("Conflicts Documents cannot be a Masked Contract");
      }

      const redlineId = updatedProposalRedline.conflicts.redlines.find(
        (documentRedline) =>
          documentRedline.currentEntry?.id?.isEqualTo(deselectedDoc.id) ||
          (!documentRedline.currentEntry && documentRedline.revisedEntry?.id?.isEqualTo(deselectedDoc.id))
      )?.field.id;

      if (!redlineId) {
        continue;
      }

      updatedProposalRedline = updatedProposalRedline.updateConflictsRedline(
        updatedProposalRedline.conflicts.removeEntryByFieldId(redlineId)
      );
    }

    onProposalRedlineChange(updatedProposalRedline, null);
  }

  function renderDocumentSelector() {
    if (Masked.isMasked(proposalRedline.conflicts)) {
      throw new Error("Conflicts Documents cannot be a Masked Contract");
    }
    let selectedTemplateIds: Array<Guid> = [];
    for (const documentRedline of proposalRedline.conflicts.redlines) {
      if (documentRedline.currentEntry) {
        for (const templateId of documentRedline.currentEntry.templateIds) {
          selectedTemplateIds.push(templateId);
        }
      }
    }

    return (
      <DocumentSelector
        documentType="conflicts"
        selectedDocumentIds={proposalRedline.conflicts.currentIds}
        selectedTemplateIds={selectedTemplateIds}
        onDocumentSelectionChanged={handleDocumentSelectionChanged}
        onCreateDocumentFromTemplate={handleBeginCreateDocumentFromTemplate}
      />
    );
  }

  function handleConflictsCheckWaiverChanged(newRedline: FieldRedline<AHBoolean>) {
    try {
      onProposalRedlineChange(
        proposalRedline.updateConflictsCheckWaivedRedline(newRedline),
        ProposalField.WaiveConflictsCheck
      );
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to update conflicts check waiver.", {
        variant: "error",
      });
    }
  }

  async function handleBeginCreateDocumentFromTemplate(templateId: Guid | undefined | null) {
    if (!templateId) return;
    setCreatingDocumentFromTemplate(templateId);
    await handleDownloadDocumentById(templateId);
  }

  async function handleBeginReplaceDocument(documentId: Guid | undefined | null) {
    if (!documentId) throw new Error("Document ID must be present to replace document");
    setDocumentIdToReplace(documentId);
    await handleDownloadDocumentById(documentId);
  }

  function handleConflictsDocumentAdded(newDocument: Document) {
    if (Masked.isMasked(proposalRedline.conflicts)) {
      throw new Error("Conflicts Documents cannot be a Masked Contract");
    }

    const newWorkDocument = WorkDocument.fromDocument(
      newDocument,
      WorkDocumentType.Conflicts,
      documentIdToReplace ?? undefined
    );

    let updatedProposalRedline = proposalRedline;
    if (
      Masked.isMasked(updatedProposalRedline.conflicts) ||
      Masked.isMasked(updatedProposalRedline.conflictsCheckWaived)
    ) {
      throw new Error("Conflicts Documents cannot be a Masked Contract");
    }

    try {
      if (documentIdToReplace) {
        updatedProposalRedline = proposalRedline.updateConflictsRedline(
          proposalRedline.conflicts.replaceEntryById(documentIdToReplace, newWorkDocument)
        );
      } else if (
        !proposalRedline.conflicts.redlines.some((document) => document.currentEntry?.is(newWorkDocument))
      ) {
        updatedProposalRedline = proposalRedline.updateConflictsRedline(
          proposalRedline.conflicts.addEntry(newWorkDocument)
        );
      }

      if (Masked.isMasked(updatedProposalRedline.conflictsCheckWaived)) {
        throw new Error("Conflicts Check Waived cannot be a Masked Contract");
      }

      if (!updatedProposalRedline.conflictsCheckWaived.currentEntry?.isEqualTo(new AHBoolean(false))) {
        updatedProposalRedline = updatedProposalRedline.updateConflictsCheckWaivedRedline(
          updatedProposalRedline.conflictsCheckWaived.edit(new AHBoolean(false))
        );
      }

      onProposalRedlineChange(updatedProposalRedline, null);
      setDocumentIdToReplace(null);
      setCreatingDocumentFromTemplate(null);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to add conflicts document.", {
        variant: "error",
      });
    }
  }

  if (Masked.isMasked(proposalRedline.conflictsCheckWaived) || Masked.isMasked(proposalRedline.conflicts)) {
    return "You do not have permission to view contracts.";
  }

  const newlyAddedConflicts = proposalRedline.conflicts.redlines.some(
    (redline) => redline.isNewlyAdded
  );
  return (
    <>
      <HeadControls>
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer active={activeReviewField?.name === ProposalFieldName.WaiveConflictsCheck}>
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.cacheKey}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <Change active={activeReviewField?.isEqualTo(ProposalField.WaiveConflictsCheck) ?? false}>
          <SwitchRedline
            disabled={disableEditing}
            boolRedline={proposalRedline.conflictsCheckWaived}
            promptTitleText={newlyAddedConflicts ? "Remove Any Newly-Added Documents?" : undefined}
            promptMessageText={
              newlyAddedConflicts
                ? "Waiving conflicts will remove any newly-added documents. Are you sure you want to proceed?"
                : undefined
            }
            okButtonText={newlyAddedConflicts ? "Remove" : undefined}
            unsetLabel="Not Waiving Conflicts Check"
            setLabel="Waiving Conflicts Check"
            onBoolRedlineChange={(newRedline) => {
              try {
                handleConflictsCheckWaiverChanged(newRedline);
              } catch (error) {
                console.error(error);
                enqueueSnackbar("Failed to update conflicts check waiver.", {
                  variant: "error",
                });
              }
            }}
          />
        </Change>
        <CommentsButton
          commentThreads={commentThreads}
          field={ProposalField.Conflicts}
          isActive={activeReviewField?.isEqualTo(ProposalField.Conflicts)}
          toolTip={commentsToolTip}
          onCommentsClicked={
            onCommentsClicked
              ? () => {
                onCommentsClicked(ProposalField.Conflicts);
              }
              : undefined
          }
        />
      </HeadControls>
      <ListContainer
        waived={
          proposalRedline.conflictsCheckWaived.currentEntry?.value ??
          proposalRedline.conflictsCheckWaived.revisedEntry?.value ??
          false
        }
        container
        direction="row"
      >
        {!disableEditing && (
          <>
            <DocumentSelectorContainer
              resizeHandles={["e"]}
              axis='x'
              width={450}
              height={Infinity}
              minConstraints={[375, Infinity]}
            >
              {renderDocumentSelector()}
            </DocumentSelectorContainer>
            <Grid>
              <Divider orientation="vertical" />
            </Grid>
          </>
        )}
        <SelectedDocsList
          workDocumentType={WorkDocumentType.Conflicts}
          documentsRedline={proposalRedline.conflicts}
          downloadingFileId={isDownloadingFile}
          commentThreads={commentThreads}
          disableEditing={disableEditing}
          activeReviewField={activeReviewField}
          commentsToolTip={commentsToolTip}
          onCommentsClicked={onCommentsClicked}
          onDownload={handleDownloadDocumentById}
          onDocumentRedlineChange={(newRedline) => {
            try {
              handleDocumentRedlineChanged(newRedline);
            } catch (error) {
              console.error(error);
              enqueueSnackbar("Failed to update conflicts document.", {
                variant: "error",
              });
            }
          }}
          onBeginReplaceDocument={handleBeginReplaceDocument}
          onDocumentClicked={(documentId: Guid) => {
            onTraverseToNewField?.(ProposalField.ConflictsDocument(documentId));
          }}
          onTraverseToNewField={onTraverseToNewField}
        />
      </ListContainer>
      {(creatingDocumentFromTemplate || documentIdToReplace) && (
        <Uploader
          session={session}
          documentType={WorkDocumentType.Conflicts}
          templateId={creatingDocumentFromTemplate ?? undefined}
          onDocumentUploaded={handleConflictsDocumentAdded}
          onClose={() => {
            setCreatingDocumentFromTemplate(null);
            setDocumentIdToReplace(null);
          }}
        />
      )}
    </>
  );
};

export default ConflictsTab;
